import { snakeCase } from 'lodash';
import { pipe } from 'ramda';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CompanyId, CompanyType } from 'api/models';
import { HoverableUserTag } from 'components/organisms/field-editors/components/controlled-select-based-editor-trigger-content';
import { flatMap, map, ofNullable, orElse, orUndefined } from 'core/maybe';
import { RootState } from 'data';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useGetAllActivePartnerCompanies } from 'queries/partner';
import { CompaniesStore, Company } from 'stores/companies';
import { Content, Empty } from 'stores/core';
import { Project } from 'stores/projects';
import { MultiSelectFieldFilter } from '../filters/multi-select';
import { SelectFilterOperationType } from '../filters/select';
import { UserEditorComponents } from './editor-components';
import { MultiSelectEditor } from './multi-select-editor';
import { useProjectIdFromUrl } from './use-user-options';

interface Props {
  filter: MultiSelectFieldFilter;
  onChange: (filter: MultiSelectFieldFilter) => void;
}

export enum ResponsibleFilter {
  MyCompany = 'MyCompany',
  NotMyCompany = 'NotMyCompany',
  All = 'All',
}

const NotMyCompanyValue = -1;

interface FilterOption {
  id: ResponsibleFilter | number;
  value: number;
  label: string;
  companyLogoUrl?: string;
}

export const ResponsibleCriteria: FC<Props> = ({ filter, onChange }) => {
  const maybeProjectId = useProjectIdFromUrl();
  const {
    user: {
      company: { companyId },
    },
  } = useUser();
  const { byId: projectsById } = useSelector(
    ({ projects_v2 }: RootState) => projects_v2,
  );
  const companiesStore = useSelector(({ companies }: RootState) => companies);
  const { companyName, companyLogoUrl } = CompaniesStore.ofId(
    CompanyId(companyId),
  )(companiesStore);
  const notMyCompany = useTranslation(
    `task_list.filters.${snakeCase(
      ResponsibleFilter.NotMyCompany,
    )}` as TranslationKey,
  );
  const all = useTranslation(
    `task_list.filters.${snakeCase(ResponsibleFilter.All)}` as TranslationKey,
  );
  const { data: activePartnerCompanies } = useGetAllActivePartnerCompanies();
  const otherCompany = pipe(
    () => maybeProjectId,
    map((projectId) => projectsById[projectId] ?? Empty()),
    flatMap(([, project]: Content<Project>) => ofNullable(project ?? null)),
    map((project: Project) => project.customer),
    map((customer: number) =>
      CompaniesStore.ofId(CompanyId(customer))(companiesStore),
    ),
    map((company: Company) => company),
    orUndefined,
  )();
  const partnerCompanies = useMemo(
    () =>
      pipe(
        () => maybeProjectId,
        map((projectId) => projectsById[projectId] ?? Empty()),
        flatMap(([, project]: Content<Project>) => ofNullable(project ?? null)),
        map((project: Project) => project.partners ?? []),
        orElse((activePartnerCompanies ?? []) as Company[]),
      )(),
    [activePartnerCompanies, maybeProjectId, projectsById],
  );
  const partnerCompanyIds = useMemo(
    () => partnerCompanies.map((company) => company.companyId),
    [partnerCompanies],
  );
  const filterOptions: FilterOption[] = useMemo(() => {
    return (
      [
        {
          id: ResponsibleFilter.MyCompany,
          value: companyId,
          label: companyName,
          imageUrl: companyLogoUrl,
        },
        {
          id: ResponsibleFilter.NotMyCompany,
          value: NotMyCompanyValue,
          label: otherCompany != null ? otherCompany.companyName : notMyCompany,
          imageUrl: otherCompany?.companyLogoUrl,
        },
      ] as unknown as FilterOption[]
    ).concat(
      partnerCompanies.map(
        (company) =>
          ({
            id: company.companyId,
            value: company.companyId,
            label: company.companyName,
            imageUrl: company.companyLogoUrl,
          } as FilterOption),
      ),
    );
  }, [
    companyId,
    companyLogoUrl,
    companyName,
    notMyCompany,
    otherCompany,
    partnerCompanies,
  ]);

  const onFilterChange = useCallback(
    (next: number[]) => {
      const values = next.reduce((finalValue, value) => {
        if (value === NotMyCompanyValue) {
          return finalValue.concat(
            pipe(
              () => maybeProjectId,
              map((projectId) => projectsById[projectId] ?? Empty()),
              flatMap(([, project]) => ofNullable(project ?? null)),
              map((project) => project.customer),
              map((customer) => [customer]),
              orElse(
                (companiesStore.ids[1] ?? [])
                  .map((x) => CompaniesStore.ofId(CompanyId(x))(companiesStore))
                  .filter(Boolean)
                  .filter(
                    (x) =>
                      x.companyId !== companyId &&
                      x.companyType !== CompanyType.Partner,
                  )
                  .map((x) => x.companyId),
              ),
            )(),
          );
        }

        return finalValue.concat(value);
      }, [] as number[]);

      onChange({
        field: filter.field,
        type: SelectFilterOperationType.IsOneOf,
        value: values,
      });
    },
    [filter, onChange, companyId, projectsById, companiesStore, maybeProjectId],
  );

  const selected = useMemo(() => {
    switch (filter.type) {
      case SelectFilterOperationType.IsOneOf: {
        const values = filter.value ?? [];

        return values.reduce((finalValues, value) => {
          if (value === companyId || partnerCompanyIds.includes(value)) {
            return finalValues.concat(value);
          }

          if (
            !finalValues.includes(NotMyCompanyValue) &&
            CompaniesStore.ofId(CompanyId(value))(companiesStore)
              .companyType !== CompanyType.Partner
          ) {
            return finalValues.concat(NotMyCompanyValue);
          }

          return finalValues;
        }, [] as number[]);
      }
      default:
        return [];
    }
  }, [companiesStore, companyId, filter, partnerCompanyIds]);

  return (
    <MultiSelectEditor
      components={UserEditorComponents}
      field={filter.field}
      filterType={SelectFilterOperationType.IsOneOf}
      options={filterOptions}
      value={selected}
      onChange={onFilterChange}
      type="user"
      placeholder={<HoverableUserTag label={all} />}
    />
  );
};
